import { X, Download, Mail, Printer, Check, ChevronDown } from 'lucide-react';
import { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { useCurrency } from '../../hooks/useCurrency';
import { useSettings } from '../../hooks/useSettings';
import { useRestaurant } from '../../contexts/RestaurantContext';

interface OrderItem {
  id: string;
  name: string;
  quantity: number;
  price: number;
  total: number;
}

interface OrderReceiptProps {
  orderId: string;
  orderDate: string;
  customerName: string;
  customerAddress: string;
  customerPhone?: string;
  customerEmail?: string;
  items: OrderItem[];
  subtotal: number;
  tax: number;
  deliveryFee: number;
  total: number;
  paymentMethod: string;
  onClose: () => void;
}

export function OrderReceipt({
  orderId,
  orderDate,
  customerName,
  customerAddress,
  customerPhone,
  customerEmail,
  items,
  subtotal,
  tax,
  deliveryFee,
  total,
  paymentMethod,
  onClose,
}: OrderReceiptProps) {
  const { formatCurrency } = useCurrency();
  const { tenantSettings, getTaxRate, getVATInclusive, getDefaultPrintFormat } = useSettings();
  const { restaurantName, restaurantLogo } = useRestaurant();
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [printMenuOpen, setPrintMenuOpen] = useState(false);
  const [vatRate, setVatRate] = useState(16);
  const [vatInclusive, setVatInclusive] = useState(false);
  const [defaultPrintFormat, setDefaultPrintFormat] = useState<'standard' | 'thermal'>('standard');
  const isTableOrderingEnabled = tenantSettings?.orderByTable ?? false;
  
  // Load VAT settings and default print format
  useEffect(() => {
    setVatRate(getTaxRate());
    setVatInclusive(getVATInclusive());
    setDefaultPrintFormat(getDefaultPrintFormat());
  }, [getTaxRate, getVATInclusive, getDefaultPrintFormat]);
  const [emailTo, setEmailTo] = useState(customerEmail || '');
  const [emailSending, setEmailSending] = useState(false);
  const [emailSent, setEmailSent] = useState(false);
  
  const handleEmailReceipt = async () => {
    if (!emailTo || !emailTo.includes('@')) {
      toast.error('Please enter a valid email address');
      return;
    }

    setEmailSending(true);
    
    // Simulate sending email (in real app, this would call an API)
    setTimeout(() => {
      setEmailSending(false);
      setEmailSent(true);
      console.log(`Receipt sent to: ${emailTo}`);
      
      // Reset after 2 seconds
      setTimeout(() => {
        setEmailSent(false);
        setShowEmailModal(false);
      }, 2000);
    }, 1500);
  };

  const handlePrint = () => {
    const printWindow = window.open('', '_blank');
    if (!printWindow) return;

    const receiptHTML = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Receipt - ${orderId}</title>
        <style>
          * {
            margin: 0;
            padding: 0;
            box-sizing: border-box;
          }
          body {
            font-family: Arial, sans-serif;
            padding: 40px;
            background: white;
            color: black;
          }
          .receipt {
            max-width: 800px;
            margin: 0 auto;
          }
          .header {
            text-center;
            margin-bottom: 40px;
            padding-bottom: 20px;
            border-bottom: 2px solid #000;
          }
          .logo {
            width: 64px;
            height: 64px;
            margin: 0 auto 15px;
            display: block;
          }
          h1 {
            font-size: 28px;
            margin-bottom: 10px;
          }
          .contact-info {
            font-size: 12px;
            color: #333;
            margin-top: 5px;
          }
          .order-info {
            display: grid;
            grid-template-columns: 1fr 1fr;
            gap: 20px;
            margin-bottom: 30px;
            padding-bottom: 20px;
            border-bottom: 1px solid #000;
          }
          .info-label {
            font-weight: bold;
            margin-bottom: 5px;
          }
          .info-value {
            color: #333;
          }
          table {
            width: 100%;
            border-collapse: collapse;
            margin-bottom: 30px;
          }
          thead {
            border-bottom: 2px solid #000;
          }
          th {
            text-align: left;
            padding: 12px 8px;
            font-weight: bold;
          }
          th:nth-child(2),
          td:nth-child(2) {
            text-align: center;
          }
          th:nth-child(3),
          th:nth-child(4),
          td:nth-child(3),
          td:nth-child(4) {
            text-align: right;
          }
          tbody tr {
            border-bottom: 1px solid #ddd;
          }
          td {
            padding: 12px 8px;
          }
          .totals {
            margin-top: 30px;
            padding-top: 20px;
            border-top: 2px solid #000;
          }
          .totals-row {
            display: flex;
            justify-content: space-between;
            margin-bottom: 10px;
          }
          .grand-total {
            font-size: 20px;
            font-weight: bold;
            padding-top: 15px;
            margin-top: 15px;
            border-top: 1px solid #000;
          }
          .footer {
            text-align: center;
            margin-top: 40px;
            padding-top: 20px;
            border-top: 1px solid #000;
            font-size: 12px;
            color: #666;
          }
          @media print {
            body {
              padding: 20px;
            }
          }
        </style>
      </head>
      <body>
        <div class="receipt">
          <div class="header">
            ${restaurantLogo ? `<img src="${restaurantLogo}" alt="Logo" class="logo" />` : ''}
            <h1>${restaurantName}</h1>
            <div class="contact-info">
              Elgon View, Eldoret, Kenya<br />
              Till No: 8804498
            </div>
          </div>

          <div class="order-info">
            <div>
              <div class="info-label">Order Number:</div>
              <div class="info-value">${orderId}</div>
            </div>
            <div>
              <div class="info-label">Date & Time:</div>
              <div class="info-value">${orderDate}</div>
            </div>
            ${!isTableOrderingEnabled ? `
            <div>
              <div class="info-label">Customer Name:</div>
              <div class="info-value">${customerName}</div>
            </div>
            ` : ''}
            <div>
              <div class="info-label">Payment Method:</div>
              <div class="info-value">${paymentMethod}</div>
            </div>
            ${!isTableOrderingEnabled ? `
            <div style="grid-column: span 2;">
              <div class="info-label">Delivery Address:</div>
              <div class="info-value">${customerAddress}</div>
              ${customerPhone ? `<div class="info-value">Phone: ${customerPhone}</div>` : ''}
            </div>
            ` : ''}
          </div>

          <table>
            <thead>
              <tr>
                <th>Item</th>
                <th>Qty</th>
                <th>Price</th>
                <th>Total</th>
              </tr>
            </thead>
            <tbody>
              ${items.map(item => `
                <tr>
                  <td>${item.name}</td>
                  <td>${item.quantity}</td>
                  <td>${formatCurrency(item.price)}</td>
                  <td>${formatCurrency(item.total)}</td>
                </tr>
              `).join('')}
            </tbody>
          </table>

          <div class="totals">
            <div class="totals-row">
              <span>Subtotal:</span>
              <span>${formatCurrency(subtotal)}</span>
            </div>
            <div class="totals-row">
              <span>VAT (${vatRate}%)${vatInclusive ? ' (Included)' : ''}:</span>
              <span>${formatCurrency(tax)}</span>
            </div>
            <div class="totals-row">
              <span>Delivery Fee:</span>
              <span>${formatCurrency(deliveryFee)}</span>
            </div>
            <div class="totals-row grand-total">
              <span>TOTAL:</span>
              <span>${formatCurrency(total)}</span>
            </div>
          </div>

          <div class="footer">
            <p>Thank you for your order!</p>
            <p style="margin-top: 10px;">For any queries, please contact us at support@restaurant.com</p>
            <p style="margin-top: 15px;">This is a computer-generated receipt. No signature required.</p>
          </div>
        </div>
      </body>
      </html>
    `;

    printWindow.document.write(receiptHTML);
    printWindow.document.close();
    printWindow.focus();
    
    // Wait for images to load before printing
    setTimeout(() => {
      printWindow.print();
      printWindow.close();
    }, 250);
  };

  const handleThermalPrint = () => {
    setPrintMenuOpen(false);
    const printWindow = window.open('', '_blank');
    if (!printWindow) return;

    const thermalReceiptHTML = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Thermal Receipt - ${orderId}</title>
        <style>
          @media print {
            @page {
              size: 80mm auto;
              margin: 0;
            }
          }
          * {
            margin: 0;
            padding: 0;
            box-sizing: border-box;
          }
          body {
            font-family: 'Courier New', monospace;
            font-size: 12px;
            line-height: 1.4;
            padding: 10px;
            max-width: 80mm;
            background: white;
            color: black;
          }
          .center {
            text-align: center;
          }
          .bold {
            font-weight: bold;
          }
          .header {
            text-align: center;
            margin-bottom: 10px;
            padding-bottom: 10px;
            border-bottom: 1px dashed #000;
          }
          .logo {
            width: 48px;
            height: 48px;
            margin: 0 auto 8px;
          }
          .store-name {
            font-size: 16px;
            font-weight: bold;
            margin-bottom: 4px;
          }
          .contact {
            font-size: 10px;
            margin-top: 4px;
          }
          .section {
            margin-bottom: 10px;
            padding-bottom: 10px;
            border-bottom: 1px dashed #000;
          }
          .info-row {
            display: flex;
            justify-content: space-between;
            margin-bottom: 3px;
            font-size: 11px;
          }
          .item-row {
            margin-bottom: 5px;
            font-size: 11px;
          }
          .item-name {
            font-weight: bold;
          }
          .item-details {
            display: flex;
            justify-content: space-between;
            margin-top: 2px;
          }
          .totals {
            margin-top: 10px;
          }
          .total-row {
            display: flex;
            justify-content: space-between;
            margin-bottom: 4px;
            font-size: 11px;
          }
          .grand-total {
            font-size: 14px;
            font-weight: bold;
            padding-top: 8px;
            margin-top: 8px;
            border-top: 1px solid #000;
          }
          .footer {
            text-align: center;
            margin-top: 15px;
            padding-top: 10px;
            border-top: 1px dashed #000;
            font-size: 10px;
          }
          .divider {
            border-top: 1px dashed #000;
            margin: 8px 0;
          }
        </style>
      </head>
      <body>
        <!-- Header -->
        <div class="header">
          ${restaurantLogo ? `<img src="${restaurantLogo}" alt="Logo" class="logo" />` : ''}
          <div class="store-name">${restaurantName}</div>
          <div class="contact">
            Elgon View, Eldoret<br/>
            Till No: 8804498
          </div>
        </div>

        <!-- Order Info -->
        <div class="section">
          <div class="info-row">
            <span>Order #:</span>
            <span class="bold">${orderId}</span>
          </div>
          <div class="info-row">
            <span>Date:</span>
            <span>${new Date().toLocaleDateString()}</span>
          </div>
          <div class="info-row">
            <span>Time:</span>
            <span>${new Date().toLocaleTimeString()}</span>
          </div>
          <div class="info-row">
            <span>Payment:</span>
            <span>${paymentMethod}</span>
          </div>
        </div>

        <!-- Customer Info -->
        ${!isTableOrderingEnabled ? `
        <div class="section">
          <div class="bold">Customer</div>
          <div class="info-row">
            <span>${customerName}</span>
          </div>
          ${customerPhone ? `<div class="info-row"><span>${customerPhone}</span></div>` : ''}
          ${customerAddress ? `<div style="font-size: 10px; margin-top: 3px;">${customerAddress}</div>` : ''}
        </div>
        ` : ''}

        <!-- Items -->
        <div class="section">
          ${items.map(item => `
            <div class="item-row">
              <div class="item-name">${item.name}</div>
              <div class="item-details">
                <span>${item.quantity} x ${formatCurrency(item.price)}</span>
                <span class="bold">${formatCurrency(item.total)}</span>
              </div>
            </div>
          `).join('')}
        </div>

        <!-- Totals -->
        <div class="totals">
          <div class="total-row">
            <span>Subtotal:</span>
            <span>${formatCurrency(subtotal)}</span>
          </div>
          <div class="total-row">
            <span>VAT (${vatRate}%)${vatInclusive ? ' (Incl)' : ''}:</span>
            <span>${formatCurrency(tax)}</span>
          </div>
          ${deliveryFee > 0 ? `
            <div class="total-row">
              <span>Delivery Fee:</span>
              <span>${formatCurrency(deliveryFee)}</span>
            </div>
          ` : ''}
          <div class="total-row grand-total">
            <span>TOTAL:</span>
            <span>${formatCurrency(total)}</span>
          </div>
        </div>

        <!-- Footer -->
        <div class="footer">
          <p>Thank you for your order!</p>
          <p style="margin-top: 8px;">support@restaurant.com</p>
          <p style="margin-top: 8px;">Powered by Qrypt POS</p>
        </div>
      </body>
      </html>
    `;

    printWindow.document.write(thermalReceiptHTML);
    printWindow.document.close();
    printWindow.focus();
    
    setTimeout(() => {
      printWindow.print();
      printWindow.close();
    }, 250);
  };

  const handleDownload = () => {
    // For now, use the same print functionality
    handlePrint();
  };

  const handlePrintDefault = () => {
    // Use the default print format from settings
    if (defaultPrintFormat === 'thermal') {
      handleThermalPrint();
    } else {
      handlePrint();
    }
  };

  return (
    <div className="fixed inset-0 bg-gray-900/20 dark:bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-in fade-in duration-200">
      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl border border-gray-200 dark:border-gray-700 w-full max-w-2xl relative max-h-[90vh] overflow-y-auto animate-in zoom-in-95 duration-200">
        {/* Header Actions */}
        <div className="sticky top-0 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 p-4 flex items-center justify-between z-10">
          <h2 className="text-xl font-bold text-gray-900 dark:text-white">Order Receipt</h2>
          <div className="flex items-center gap-2">
            {/* Print Button - Uses Default Format */}
            <button
              onClick={handlePrintDefault}
              className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors text-sm font-medium"
            >
              <Printer className="w-4 h-4" />
              Print
            </button>

            {/* Print Format Dropdown */}
            <div className="relative">
              <button
                onClick={() => setPrintMenuOpen(!printMenuOpen)}
                className="flex items-center gap-1 px-3 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors text-sm font-medium"
                title="Choose print format"
              >
                <ChevronDown className="w-4 h-4" />
              </button>
              
              {printMenuOpen && (
                <>
                  <div className="fixed inset-0 z-10" onClick={() => setPrintMenuOpen(false)} />
                  <div className="absolute right-0 mt-2 w-56 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg z-20">
                    <button
                      onClick={() => { handlePrint(); setPrintMenuOpen(false); }}
                      className="w-full text-left px-4 py-3 text-sm hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors rounded-t-lg flex items-center gap-3 text-gray-900 dark:text-white"
                    >
                      <span className="text-lg">📄</span>
                      <div>
                        <div className="font-medium">Standard (A4)</div>
                        <div className="text-xs text-gray-500 dark:text-gray-400">800mm width</div>
                      </div>
                    </button>
                    <button
                      onClick={() => { handleThermalPrint(); setPrintMenuOpen(false); }}
                      className="w-full text-left px-4 py-3 text-sm hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors rounded-b-lg flex items-center gap-3 text-gray-900 dark:text-white border-t border-gray-100 dark:border-gray-700"
                    >
                      <span className="text-lg">🖨️</span>
                      <div>
                        <div className="font-medium">Thermal (80mm)</div>
                        <div className="text-xs text-gray-500 dark:text-gray-400">For POS printers</div>
                      </div>
                    </button>
                  </div>
                </>
              )}
            </div>

            <button
              onClick={handleDownload}
              className="flex items-center gap-2 px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors text-sm font-medium"
            >
              <Download className="w-4 h-4" />
              Download
            </button>
            <button
              onClick={() => setShowEmailModal(true)}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium"
            >
              <Mail className="w-4 h-4" />
              Email
            </button>
            <button
              onClick={onClose}
              className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Receipt Content */}
        <div className="p-8">
          {/* Restaurant Header */}
          <div className="text-center mb-8 border-b-2 border-gray-300 dark:border-gray-600 pb-6">
            {restaurantLogo && (
              <img 
                src={restaurantLogo} 
                alt="Restaurant Logo" 
                className="w-16 h-16 mx-auto mb-3 object-contain"
              />
            )}
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
              {restaurantName}
            </h1>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Elgon View, Eldoret, Kenya
            </p>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Tel: +254 704 930 169 | Email: info@restaurant.com
            </p>
          </div>

          {/* Order Information */}
          <div className="mb-6 pb-4 border-b border-gray-200 dark:border-gray-700">
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <p className="text-gray-600 dark:text-gray-400 font-semibold mb-1">Order Number:</p>
                <p className="text-gray-900 dark:text-white font-mono">{orderId}</p>
              </div>
              <div>
                <p className="text-gray-600 dark:text-gray-400 font-semibold mb-1">Date & Time:</p>
                <p className="text-gray-900 dark:text-white">{orderDate}</p>
              </div>
              {/* <div>
                <p className="text-gray-600 dark:text-gray-400 font-semibold mb-1">Customer Name:</p>
                <p className="text-gray-900 dark:text-white">{customerName}</p>
              </div> */}
              <div>
                <p className="text-gray-600 dark:text-gray-400 font-semibold mb-1">Payment Method:</p>
                <p className="text-gray-900 dark:text-white">{paymentMethod}</p>
              </div>
            </div>
            <div className="mt-4">
              <p className="text-gray-600 dark:text-gray-400 font-semibold mb-1">Delivery Address:</p>
              <p className="text-gray-900 dark:text-white">{customerAddress}</p>
              {customerPhone && (
                <p className="text-gray-900 dark:text-white">Phone: {customerPhone}</p>
              )}
            </div>
          </div>

          {/* Order Items */}
          <div className="mb-6">
            <table className="w-full">
              <thead>
                <tr className="border-b-2 border-gray-300 dark:border-gray-600">
                  <th className="text-left py-3 text-gray-900 dark:text-white font-bold">Item</th>
                  <th className="text-center py-3 text-gray-900 dark:text-white font-bold">Qty</th>
                  <th className="text-right py-3 text-gray-900 dark:text-white font-bold">Price</th>
                  <th className="text-right py-3 text-gray-900 dark:text-white font-bold">Total</th>
                </tr>
              </thead>
              <tbody>
                {items.map((item) => (
                  <tr key={item.id} className="border-b border-gray-200 dark:border-gray-700">
                    <td className="py-3 text-gray-900 dark:text-white">{item.name}</td>
                    <td className="py-3 text-center text-gray-900 dark:text-white">{item.quantity}</td>
                    <td className="py-3 text-right text-gray-900 dark:text-white">
                      {formatCurrency(item.price)}
                    </td>
                    <td className="py-3 text-right text-gray-900 dark:text-white font-semibold">
                      {formatCurrency(item.total)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Totals */}
          <div className="border-t-2 border-gray-300 dark:border-gray-600 pt-4 mb-6">
            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-gray-600 dark:text-gray-400">Subtotal:</span>
                <span className="text-gray-900 dark:text-white">{formatCurrency(subtotal)}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-600 dark:text-gray-400">VAT ({vatRate}%){vatInclusive ? ' (Included)' : ''}:</span>
                <span className="text-gray-900 dark:text-white">{formatCurrency(tax)}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-600 dark:text-gray-400">Delivery Fee:</span>
                <span className="text-gray-900 dark:text-white">{formatCurrency(deliveryFee)}</span>
              </div>
              <div className="flex justify-between text-xl font-bold pt-3 border-t border-gray-300 dark:border-gray-600">
                <span className="text-gray-900 dark:text-white">TOTAL:</span>
                <span className="text-gray-900 dark:text-white">{formatCurrency(total)}</span>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="text-center pt-6 border-t border-gray-200 dark:border-gray-700">
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
              Thank you for your order!
            </p>
            <p className="text-xs text-gray-500 dark:text-gray-500">
              For any queries, please contact us at support@restaurant.com
            </p>
            <p className="text-xs text-gray-500 dark:text-gray-500 mt-4">
              This is a computer-generated receipt. No signature required.
            </p>
          </div>
        </div>
      </div>

      {/* Email Modal */}
      {showEmailModal && (
        <div className="fixed inset-0 bg-gray-900/50 dark:bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4 animate-in fade-in duration-200">
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl border border-gray-200 dark:border-gray-700 w-full max-w-2xl relative max-h-[90vh] overflow-y-auto animate-in zoom-in-95 duration-200">
            {/* Header Actions */}
            <div className="sticky top-0 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 p-4 flex items-center justify-between z-10">
              <h2 className="text-xl font-bold text-gray-900 dark:text-white">Email Receipt</h2>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setShowEmailModal(false)}
                  className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>

            {/* Email Content */}
            <div className="p-8">
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Email To:</label>
                <input
                  type="email"
                  value={emailTo}
                  onChange={(e) => setEmailTo(e.target.value)}
                  className="mt-1 block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                />
              </div>
              <div className="flex justify-end">
                <button
                  onClick={handleEmailReceipt}
                  className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium"
                >
                  {emailSending ? (
                    <svg className="animate-spin h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.928l3-2.647z"></path>
                    </svg>
                  ) : emailSent ? (
                    <Check className="w-4 h-4" />
                  ) : (
                    <Mail className="w-4 h-4" />
                  )}
                  {emailSending ? 'Sending...' : emailSent ? 'Sent!' : 'Send'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}